import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { EditorDataService } from '../editor-data/editor-data.service';
import { EditorEligibilityRuleHasBookingOption } from '../editor-eligibility-rule/editor-eligibility-rule-has-booking-option.model';


@Component({
    selector: 'eas-editor-eligibility-rule-value-has-booking-option',
    templateUrl: './editor-eligibility-rule-value.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditorEligibilityRuleValueHasBookingOptionComponent implements OnInit, OnChanges, OnDestroy {
  @Input() rule: EditorEligibilityRuleHasBookingOption;
  @Input() affirmative: boolean;

  private reset$ = new Subject<void>();
  private destroyed$ = new Subject<void>();

  bookingItemNames: { id: string, name: string }[];
  bookingOptionNames: { id: string, name: string }[];
  bookingStateNames: { id: string, name: string }[];
  bookingMetadataNames: { id: string, name: string }[];

  value: string;

  constructor(private editorData: EditorDataService) {

  }

  ngOnInit() {
    this.editorData.bookingItemNames$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        bookingItemNames => {
          this.bookingItemNames = bookingItemNames;
          this.value = this.getValue();
        }
      );

    this.editorData.bookingStateNames$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        bookingStateNames => {
          this.bookingStateNames = bookingStateNames;
          this.value = this.getValue();
        }
      );

    this.editorData.bookingMetadataNames$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        bookingMetadataNames => {
          this.bookingMetadataNames = bookingMetadataNames;
          this.value = this.getValue();
        }
      );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.rule) {
      this.reset$.next();
      this.editorData.bookingOptionNamesForBookingItem$(this.rule.bookingItem)
        .pipe(
          takeUntil(this.reset$),
          takeUntil(this.destroyed$)
        )
        .subscribe(
          bookingOptionNames => {
            this.bookingOptionNames = bookingOptionNames;
            this.value = this.getValue();
          }
        );
    }

    if (changes.rule || changes.affirmative) {
      this.value = this.getValue();
    }
  }

  ngOnDestroy() {
    this.reset$.complete();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  getValue(): string {
    if (this.bookingItemNames === undefined
      || this.bookingOptionNames === undefined
      || this.bookingStateNames === undefined
      || this.bookingMetadataNames === undefined
    ) {
      return $localize `Not available`;
    }

    const bookingOptions = this.rule.getBookingOptionsAsString(this.bookingOptionNames);
    let value = this.affirmative
      ? $localize `Candidate has one of the booking options ${bookingOptions}`
      : $localize `Candidate has NONE of the booking options ${bookingOptions}`;

    const bookingItem = this.rule.getBookingItemName(this.bookingItemNames);
    value = $localize `${value} of booking item ${bookingItem}`;

    const bookingStates = this.rule.stateNamesString;
    // const bookingStates = this.rule.getBookingStatesAsString(this.bookingStateNames);
    if (bookingStates !== '') {
      value = this.affirmative
        ? $localize `${value} in one of the states ${bookingStates}`
        : $localize `${value} in any of the states ${bookingStates}`;
    }

    const bookingMetadata = this.rule.getBookingMetadataAsString(this.bookingMetadataNames);
    if (bookingMetadata !== '') {
      value = $localize `${value} with metadata ${bookingMetadata}`;
    }

    return value;
  }
}
